import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Button, ConfirmDialog, Field, PanelBody, PanelHeader, SidePanel,
} from '../kit';
import { clearLocationCache, createLocation, deleteLocation, updateLocation } from '../../../utils/locationApi';
import { apiErrorMessage, getCurrencies } from '../../../utils/organizationApi';
import { SUPPORTED_CURRENCIES } from '../../../utils/currency';
import {
  applyLocationChange, currencyOptions, locationChangeCount, locationFormFromRecord, locationPatch, locationPayload,
  validateLocation,
} from './orgModel';
import { CurrencySelect, TimezoneSelect } from './orgsUi';

/**
 * Add / edit one location — every field of the Location record the admin
 * manages. Only changed fields are sent on update.
 */
export default function LocationPanel({ open, org, location, onClose, onSaved, onDeleted }) {
  const isNew = !location;
  const uid = useId();
  const id = (name) => `${uid}-${name}`;
  const [original, setOriginal] = useState(() => locationFormFromRecord(location));
  const [form, setForm] = useState(() => locationFormFromRecord(location));
  const [errors, setErrors] = useState({});
  const [suggested, setSuggested] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const touched = useRef({});

  useEffect(() => {
    if (!open) return;
    const f = locationFormFromRecord(location);
    setOriginal(f);
    setForm(f);
    setErrors({});
    setSuggested({});
    touched.current = {};
  }, [open, location]);

  useEffect(() => {
    if (open && !currencies.length) getCurrencies().then(setCurrencies).catch(() => {});
  }, [open, currencies.length]);

  const options = useMemo(
    () => currencyOptions(currencies, [...SUPPORTED_CURRENCIES, form.currency].filter(Boolean)),
    [currencies, form.currency]
  );
  const changes = isNew ? (form.name || form.code ? 1 : 0) : locationChangeCount(original, form);

  const set = (field, value) => {
    if (field === 'currency' || field === 'timezone') touched.current[field] = true;
    const { form: next, suggested: s } = applyLocationChange(form, field, value, touched.current);
    setForm(next);
    if (Object.keys(s).length) setSuggested((old) => ({ ...old, ...s }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const requestClose = () => {
    if (saving || deleting) return;
    if (changes > 0) setConfirmDiscard(true);
    else onClose();
  };

  const save = async () => {
    const next = validateLocation(form);
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      if (isNew) {
        await createLocation({ ...locationPayload(form), orgId: org.id });
        toast.success(`${form.name.trim()} added`);
      } else {
        await updateLocation({ id: location.id, orgId: org.id, ...locationPatch(original, form) });
        toast.success(`${form.name.trim()} saved`);
      }
      clearLocationCache(org.id);
      await onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the location.'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteLocation(location.id);
      clearLocationCache(org.id);
      toast.success(`${location.name || 'Location'} deleted`);
      setConfirmDelete(false);
      await onDeleted();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete the location.'));
    } finally {
      setDeleting(false);
    }
  };

  const text = (name, label, extra = {}) => (
    <Field label={label} required={extra.required} error={errors[name]} help={extra.help} htmlFor={id(name)}>
      <input
        id={id(name)}
        className={`ak-input${extra.mono ? ' ak-mono' : ''}`}
        type={extra.type || 'text'}
        value={form[name]}
        placeholder={extra.placeholder}
        inputMode={extra.inputMode}
        step={extra.step}
        spellCheck={extra.mono ? false : undefined}
        autoComplete="off"
        aria-invalid={Boolean(errors[name])}
        onChange={(e) => set(name, e.target.value)}
      />
    </Field>
  );

  const currencyHelp = suggested.currency && suggested.currency === form.currency
    ? <span className="orgs-suggest">Suggested from country</span>
    : 'Used to format money for this property.';
  const timezoneHelp = suggested.timezone && suggested.timezone === form.timezone
    ? <span className="orgs-suggest">Suggested from country · sets the expected night-audit business date</span>
    : 'Sets the property\'s expected night-audit business date.';

  return (
    <>
      <SidePanel open={open} onRequestClose={requestClose} label={isNew ? 'Add location' : `Edit ${location?.name || 'location'}`} narrow>
        <PanelHeader
          title={isNew ? 'Add location' : (location?.name || 'Unnamed location')}
          sub={<span className="ak-mono">{isNew ? `New property · ${org.name}` : `${location.code || '—'} · #${location.id} · ${org.name}`}</span>}
          onClose={requestClose}
          bordered
        />
        <PanelBody>
          <section className="orgs-section" aria-label="Property">
            <span className="ak-section-label">Property</span>
            <div className="ak-grid ak-2">
              {text('name', 'Name', { required: true })}
              {text('code', 'Code', { required: true })}
            </div>
          </section>

          <section className="orgs-section" aria-label="Address and region">
            <span className="ak-section-label">Address &amp; region</span>
            {text('address', 'Street')}
            <div className="ak-grid ak-2">
              {text('city', 'City')}
              {text('state', 'State / region')}
              {text('zip', 'Postal code')}
              {text('country', 'Country', { placeholder: 'e.g. US or United States' })}
            </div>
            <div className="ak-grid ak-2">
              <Field label="Timezone" required error={errors.timezone} help={timezoneHelp}>
                <TimezoneSelect value={form.timezone} onChange={(v) => set('timezone', v)} invalid={Boolean(errors.timezone)} />
              </Field>
              <Field label="Currency" help={currencyHelp} htmlFor={id('currency')}>
                <CurrencySelect
                  id={id('currency')}
                  value={form.currency}
                  options={options}
                  includeDefault
                  defaultLabel="Not set (USD)"
                  onChange={(v) => set('currency', v)}
                />
              </Field>
            </div>
          </section>

          <section className="orgs-section" aria-label="Contact">
            <span className="ak-section-label">Contact</span>
            <div className="ak-grid ak-2">
              {text('phoneNumber', 'Phone', { type: 'tel' })}
              {text('websiteUrl', 'Website', { type: 'url', placeholder: 'https://' })}
            </div>
          </section>

          <section className="orgs-section" aria-label="Linked systems">
            <span className="ak-section-label">Linked systems</span>
            <div className="ak-grid ak-2">
              {text('accountingId', 'Accounting ID', { placeholder: 'Not linked' })}
              {text('payrollId', 'Payroll ID', { placeholder: 'Not linked' })}
              {text('timekeepingId', 'Timekeeping ID', { placeholder: 'Not linked' })}
              {text('lighthouseId', 'Lighthouse ID', { placeholder: 'Not linked' })}
            </div>
            {text('googlePlaceId', 'Google Place ID', {
              mono: true,
              placeholder: 'ChIJ…',
              help: (
                <>
                  Pulls reviews and ratings. Find it with{' '}
                  <a className="orgs-help-link" href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-id" target="_blank" rel="noopener noreferrer">
                    Google's Place ID finder
                  </a>.
                </>
              ),
            })}
          </section>

          <section className="orgs-section" aria-label="Other">
            <span className="ak-section-label">Other</span>
            <div className="ak-grid ak-2">
              {text('latitude', 'Latitude', { type: 'number', step: 'any', inputMode: 'decimal' })}
              {text('longitude', 'Longitude', { type: 'number', step: 'any', inputMode: 'decimal' })}
            </div>
            <label className="orgs-check">
              <input type="checkbox" className="ak-check" checked={form.wheelchairAccessible} onChange={(e) => set('wheelchairAccessible', e.target.checked)} />
              <span>Wheelchair accessible</span>
            </label>
          </section>
        </PanelBody>
        <footer className="ak-panel-foot">
          {!isNew ? (
            <Button variant="danger" className="ak-ghost" onClick={() => setConfirmDelete(true)} disabled={saving}>Delete location</Button>
          ) : null}
          {!isNew && changes > 0 ? <span className="ak-dirty">{changes} unsaved change{changes === 1 ? '' : 's'}</span> : null}
          <span className="ak-foot-spacer" />
          <Button onClick={requestClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving || (!isNew && changes === 0)}>
            {saving ? 'Saving…' : isNew ? 'Add location' : 'Save'}
          </Button>
        </footer>
      </SidePanel>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        danger
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      >
        <p style={{ margin: 0 }}>{isNew ? 'This location hasn\'t been added yet.' : `Your ${changes} change${changes === 1 ? '' : 's'} to ${location?.name || 'this location'} will be lost.`}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${location?.name || 'this location'}?`}
        confirmLabel="Delete location"
        danger
        busy={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={doDelete}
      >
        <p style={{ margin: 0 }}>
          This is a soft delete: the location's data stays in the database, but it no longer appears in dashboards, reports or location pickers.
          There's no restore button here.
        </p>
      </ConfirmDialog>
    </>
  );
}
